import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutGrid, Sparkles, Warehouse, Map, Boxes, Tags, ScanLine,
  Search, ClipboardList, SlidersHorizontal, CloudOff, Settings, LogOut, PlayCircle,
  ArrowLeftRight, ScrollText, RefreshCw, ClipboardCheck, PackageOpen, ShieldCheck, ShieldAlert, Forklift, Inbox, PackageSearch, Truck, Upload,
  Menu, X, PanelLeftClose, PanelLeftOpen,
} from 'lucide-react';
import { useApp } from '../store/useApp';
import { Logo } from './Logo';
import { contarPendientes } from '../offline/db';
import { arrancarAutoSync } from '../offline/sync';
import { fmtHora } from '../lib/format';
import { TrialBanner } from './TrialBanner';
import { LegalFooter } from './LegalFooter';
import { LangSelector } from './LangSelector';
import { Ayuda } from './Ayuda';
import { BottomBar } from './BottomBar';

const ADMIN = ['owner', 'admin'];
const OP = ['owner', 'admin', 'operario'];
const TODOS = ['owner', 'admin', 'operario', 'lectura'];

const ITEMS = [
  { to: '/', key: 'panel', icon: LayoutGrid, roles: TODOS, end: true },
  { to: '/onboarding', key: 'onboarding', icon: Sparkles, roles: ADMIN },
  { to: '/tutoriales', key: 'tutoriales', icon: PlayCircle, roles: TODOS },
  { to: '/politicas', key: 'politicas', icon: SlidersHorizontal, roles: ADMIN },
  { to: '/inventario', key: 'inventario', icon: ClipboardCheck, roles: OP },
  { to: '/carga-inicial', key: 'carga_inicial', icon: Upload, roles: ADMIN },
  { to: '/entrada', key: 'entrada', icon: Forklift, roles: OP },
  { to: '/recepciones', key: 'recepciones', icon: Inbox, roles: OP },
  { to: '/pedidos', key: 'pedidos', icon: PackageOpen, roles: OP },
  { to: '/almacenes', key: 'almacenes', icon: Warehouse, roles: ADMIN },
  { to: '/mapa', key: 'mapa', icon: Map, roles: TODOS },
  { to: '/ubicaciones', key: 'ubicaciones', icon: Boxes, roles: TODOS },
  { to: '/etiquetas', key: 'etiquetas', icon: Tags, roles: ADMIN },
  { to: '/escanear', key: 'escanear', icon: ScanLine, roles: OP },
  { to: '/buscar', key: 'buscar', icon: Search, roles: TODOS },
  { to: '/existencias', key: 'existencias', icon: PackageSearch, roles: TODOS },
  { to: '/traspaso', key: 'traspaso', icon: Truck, roles: OP },
  { to: '/movimientos', key: 'movimientos', icon: ArrowLeftRight, roles: OP },
  { to: '/recuento', key: 'recuento', icon: ClipboardList, roles: OP },
  { to: '/auditoria', key: 'auditoria', icon: ScrollText, roles: ADMIN },
  { to: '/retirada', key: 'retirada', icon: ShieldAlert, roles: ADMIN },
  { to: '/ajustes', key: 'ajustes', icon: Settings, roles: ADMIN },
];

export default function Layout({ children }) {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const { tenantActivo, tenants, elegirTenant, salir, pendientes, setPendientes, catalogoFecha, refrescarCatalogo, esSuperadmin, soporte, salirSoporte } = useApp();
  const [online, setOnline] = useState(navigator.onLine);
  const [sincronizando, setSincronizando] = useState(false);
  // Menú: colapsado (solo ≥1280, plegado a iconos, se recuerda) y abierto (drawer <1280, no persiste).
  const [colapsado, setColapsado] = useState(() => localStorage.getItem('hueco-menu-colapsado') === '1');
  const [abierto, setAbierto] = useState(false);
  const rol = tenantActivo?.rol || 'lectura';

  const alternarColapsado = () => setColapsado((v) => { const n = !v; localStorage.setItem('hueco-menu-colapsado', n ? '1' : '0'); return n; });
  const cerrarDrawer = () => setAbierto(false);
  // Selector de empresa: elegir un tenant o crear otro nuevo (opción "＋ Crear otra empresa").
  const cambiarTenant = (val) => {
    if (val === '__nueva__') { nav('/login?m=empresa'); return; }
    elegirTenant(val);
  };

  // Cierra el drawer con Escape (accesibilidad en tablet con teclado).
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') setAbierto(false); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, []);

  useEffect(() => {
    contarPendientes().then(setPendientes);
    arrancarAutoSync(setPendientes);
    if (navigator.onLine) refrescarCatalogo();
    const on = () => { setOnline(true); refrescarCatalogo(true); }; const off = () => setOnline(false);
    const foco = () => { if (navigator.onLine) refrescarCatalogo(); };
    window.addEventListener('online', on); window.addEventListener('offline', off);
    window.addEventListener('focus', foco);
    return () => {
      window.removeEventListener('online', on); window.removeEventListener('offline', off);
      window.removeEventListener('focus', foco);
    };
  }, [setPendientes, refrescarCatalogo]);

  const sincronizar = async () => { setSincronizando(true); await refrescarCatalogo(true); setSincronizando(false); };

  const visibles = ITEMS.filter((i) => i.roles.includes(rol));

  return (
    <div className={`shell${colapsado ? ' menu-colapsado' : ''}${abierto ? ' menu-abierto' : ''}`}>
      {soporte && (
        <div className="soporte-banda" data-testid="soporte-banda">
          {t('admin.viendo_como', { empresa: soporte.nombre })}
          <button className="soporte-salir" onClick={() => { salirSoporte(); nav('/admin'); }} data-testid="soporte-salir-btn">{t('admin.salir_soporte')}</button>
        </div>
      )}
      {!online && <div className="offline-strip" data-testid="offline-strip">{t('comun.sin_conexion')}</div>}
      <header className="topbar">
        <button className="btn ghost sm menu-hamburguesa" onClick={() => setAbierto(true)} data-testid="menu-abrir" title={t('nav.panel')} aria-label="menu">
          {/* SVG inline (no lucide): garantiza que iOS Safari nunca colapse el icono a ancho 0 */}
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: '0 0 auto', display: 'block' }} aria-hidden="true">
            <line x1="4" y1="6" x2="20" y2="6" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="18" x2="20" y2="18" />
          </svg>
        </button>
        <button className="row" style={{ background: 'none', border: 0, cursor: 'pointer' }} onClick={() => nav('/')} data-testid="logo-home">
          <Logo />
        </button>
        <div style={{ flex: 1 }} />
        <Ayuda />
        {OP.includes(rol) && (
          <button className="btn ghost sm" onClick={() => nav('/pendientes')} title={t('nav.pendientes')} data-testid="pendientes-header" style={{ position: 'relative', gap: 6 }}>
            <CloudOff size={16} strokeWidth={2} />
            {pendientes > 0 && <span className="badge-num" data-testid="pendientes-contador">{pendientes}</span>}
          </button>
        )}
        <LangSelector compacto />
        <button className="btn ghost sm" onClick={sincronizar} disabled={sincronizando || !online}
          title={t('comun.sincronizar')} data-testid="sync-catalogo-btn" style={{ gap: 6 }}>
          <RefreshCw size={15} strokeWidth={2} className={sincronizando ? 'girando' : ''} />
          <span className="lbl-cat small muted" data-testid="catalogo-fecha">
            {catalogoFecha ? fmtHora(new Date(catalogoFecha).toISOString(), i18n.language) : t('comun.sin_sincronizar')}
          </span>
        </button>
        {tenants.length >= 1 && (
          <select value={tenantActivo?.tenant_id || ''} onChange={(e) => cambiarTenant(e.target.value)} style={{ width: 'auto' }} data-testid="tenant-select">
            {tenants.map((x) => <option key={x.tenant_id} value={x.tenant_id}>{x.nombre}</option>)}
            <option value="__nueva__">{`＋ ${t('login.crear_otra_empresa')}`}</option>
          </select>
        )}
        <button className="btn ghost sm" onClick={async () => { await salir(); nav('/login'); }} data-testid="logout-btn" title={t('nav.salir')}>
          <LogOut size={16} strokeWidth={2} />
        </button>
      </header>
      <div className="wrap">
        <div className="side-backdrop" onClick={cerrarDrawer} data-testid="menu-backdrop" />
        <nav className="side" data-testid="sidebar">
          <div className="side-top">
            <button className="btn ghost sm menu-colapsar" onClick={alternarColapsado} data-testid="menu-colapsar"
              title={colapsado ? t('comun.aplicar') : t('comun.cerrar')} aria-label="colapsar-menu">
              {colapsado ? <PanelLeftOpen size={18} strokeWidth={2} /> : <PanelLeftClose size={18} strokeWidth={2} />}
            </button>
            <button className="btn ghost sm menu-cerrar-drawer" onClick={cerrarDrawer} data-testid="menu-cerrar" aria-label="cerrar-menu">
              <X size={18} strokeWidth={2} />
            </button>
          </div>
          {visibles.map((i) => (
            <NavLink key={i.to} to={i.to} end={i.end} onClick={cerrarDrawer} title={t(`nav.${i.key}`)}
              className={({ isActive }) => `navlink ${isActive ? 'activo' : ''}`}
              data-testid={`nav-${i.key}`}>
              <i.icon size={20} strokeWidth={2} />
              <span className="lbl">{t(`nav.${i.key}`)}</span>
              {i.contador && pendientes > 0 && <span className="badge-num" data-testid="pendientes-contador">{pendientes}</span>}
            </NavLink>
          ))}
          {esSuperadmin && (
            <NavLink to="/admin" onClick={cerrarDrawer} title={t('admin.menu')} className={({ isActive }) => `navlink ${isActive ? 'activo' : ''}`} data-testid="nav-admin">
              <ShieldCheck size={20} strokeWidth={2} />
              <span className="lbl">{t('admin.menu')}</span>
            </NavLink>
          )}
          {/* En móvil: idioma, empresa y sincronización van DENTRO del menú (la cabecera solo deja logo/menú/salir) */}
          <div className="side-mobile-extra" data-testid="menu-extra">
            <LangSelector />
            {tenants.length >= 1 && (
              <select value={tenantActivo?.tenant_id || ''} onChange={(e) => { cambiarTenant(e.target.value); cerrarDrawer(); }} data-testid="menu-tenant-select">
                {tenants.map((x) => <option key={x.tenant_id} value={x.tenant_id}>{x.nombre}</option>)}
                <option value="__nueva__">{`＋ ${t('login.crear_otra_empresa')}`}</option>
              </select>
            )}
            <button className="btn ghost sm" onClick={sincronizar} disabled={sincronizando || !online} data-testid="menu-sync-btn" style={{ justifyContent: 'flex-start', gap: 8 }}>
              <RefreshCw size={15} strokeWidth={2} className={sincronizando ? 'girando' : ''} />
              <span className="small muted">{catalogoFecha ? fmtHora(new Date(catalogoFecha).toISOString(), i18n.language) : t('comun.sin_sincronizar')}</span>
            </button>
          </div>
        </nav>
        <main className="content"><TrialBanner />{children}<LegalFooter /></main>
      </div>
      <BottomBar rol={rol} abrirMenu={() => setAbierto(true)} />
    </div>
  );
}
